import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, Filter, Plus, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  adminInputClass,
  filterPillClass,
  filterPillActiveClass,
  StatusPill,
  TableFilterCard,
  TableSkeleton,
} from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingEmpty,
  AccountingError,
} from '@/components/admin/accounting/AccountingShell';
import BulkDeleteDialog, { BulkDeleteBar } from '@/components/admin/accounting/BulkDeleteDialog';
import useRowSelection from '@/hooks/useRowSelection';
import InvoiceFormDialog from '@/components/admin/accounting/InvoiceFormDialog';
import InvoiceActionsMenu, {
  invoiceDetailPath,
  useInvoiceWorkflow,
} from '@/components/admin/accounting/InvoiceWorkflow';
import {
  accountingError,
  activeFilterCount,
  filtersFromSearch,
  formatCents,
  invoiceStatusMeta,
  searchFromFilters,
} from '@/lib/accounting';
import {
  deleteAccountingInvoice,
  getAccountingCustomers,
  getAccountingInvoiceSummary,
  getAccountingInvoices,
  getAccountingItems,
  getAccountingTaxes,
} from '@/lib/api';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS = {
  tab: 'unpaid',
  customer: '',
  status: 'all',
  from: '',
  to: '',
  number: '',
  page: '1',
};

// The tab is which list you are looking at, not a filter, so it is excluded
// from the count shown on the Filters pill.
const COUNTED_FILTERS = { customer: '', status: 'all', from: '', to: '', number: '' };

const TABS = [
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'draft', label: 'Draft' },
  { id: 'all', label: 'All Invoices' },
];

function SummaryTile({ label, value, hint, tone }) {
  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardContent className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
          {label}
        </p>
        <p
          className={cn(
            'mt-2 text-2xl font-semibold',
            tone === 'red' ? 'text-red-700' : 'text-[#062B1A]'
          )}
        >
          {value}
        </p>
        {hint && <p className="mt-1 text-xs text-[#062B1A]/45">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export default function AccountingInvoices() {
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => filtersFromSearch(location.search, DEFAULT_FILTERS));
  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [tabCounts, setTabCounts] = useState({ unpaid: 0, draft: 0, all: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [permissions, setPermissions] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // An application invoice is a document the application workflow owns, so it
  // is never selectable here — it is withdrawn by revoking that document.
  const selection = useRowSelection(rows, {
    keyOf: (row) => row.ref,
    // The server's verdict, the same one the row button uses, so a row that
    // offers Delete can also be selected for a bulk one. Gating on origin
    // meant no invoice in this portal could be selected at all: every one
    // of them came from an application.
    canSelect: (row) => row.delete_effect?.allowed !== false,
  });

  const filterCount = activeFilterCount(filters, COUNTED_FILTERS);

  // Filters live in the URL so a filtered view can be shared and survives a
  // reload. The list is driven from that state, never from the inputs directly.
  useEffect(() => {
    const search = searchFromFilters(filters, DEFAULT_FILTERS);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
  }, [filters, location.pathname, location.search, navigate]);

  const queryParams = useMemo(
    () => ({
      tab: filters.tab,
      customer: filters.customer || undefined,
      status: filters.status === 'all' ? undefined : filters.status,
      from: filters.from || undefined,
      to: filters.to || undefined,
      number: filters.number || undefined,
      page: Number(filters.page) || 1,
    }),
    [filters]
  );

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const [list, totals] = await Promise.all([
          getAccountingInvoices(queryParams),
          getAccountingInvoiceSummary({ ...queryParams, tab: undefined, page: undefined }),
        ]);
        setRows(list.data?.items || []);
        setPageInfo(list.data?.page_info || null);
        setTabCounts(list.data?.tab_counts || { unpaid: 0, draft: 0, all: 0 });
        setPermissions(list.data?.permissions || {});
        setPaymentMethods(list.data?.payment_methods || []);
        setSummary(totals.data || null);
      } catch (err) {
        setError(accountingError(err, 'Invoices could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [queryParams]
  );

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [customerList, itemList, taxList] = await Promise.all([
          getAccountingCustomers({ page_size: 100 }),
          getAccountingItems({ context: 'sell', status: 'active', page_size: 100 }),
          getAccountingTaxes(),
        ]);
        if (cancelled) return;
        setCustomers(customerList.data?.items || []);
        setItems(itemList.data?.items || []);
        setTaxes(taxList.data?.items || []);
      } catch {
        // The pickers degrade to empty; the list itself still works and shows
        // its own error if the list request failed.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch, page: '1' }));

  // Every action on a row — view, edit, duplicate, record payment, send,
  // export, void, delete — comes from one hook, shared with the invoice page,
  // so a row here and that page cannot end up meaning different things by the
  // same word.
  const workflow = useInvoiceWorkflow({
    reload: () => load({ quiet: true }),
    customers,
    items,
    taxes,
    paymentMethods,
  });

  const canCreate = permissions.create_invoice === true;
  const canDelete = permissions.delete_invoice === true;

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={() => load({ quiet: true })} disabled={refreshing}>
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {canCreate && (
        <Button type="button" onClick={() => setCreateOpen(true)} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Plus className="mr-2 size-4" />
          Create an invoice
        </Button>
      )}
    </>
  );

  return (
    <AccountingShell
      title="Invoices"
      description="Every invoice the college has raised — those issued from an application and those raised here — with what is still owed on each."
      actions={actions}
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryTile
          label="Overdue"
          value={summary ? summary.overdue.display : '—'}
          hint="Past its due date and unpaid"
          tone="red"
        />
        <SummaryTile
          label="Due within next 30 days"
          value={summary ? summary.due_next_30_days.display : '—'}
          hint="Issued and not yet due"
        />
        <SummaryTile
          label="Average time to get paid"
          value={summary ? summary.average_days_to_get_paid.display : '—'}
          hint={
            summary && !summary.average_days_to_get_paid.available
              ? 'No settled invoices yet'
              : 'Across invoices settled in full'
          }
        />
        <SummaryTile
          label="Upcoming payout"
          value={summary ? summary.upcoming_payout.display : '—'}
          hint={summary?.upcoming_payout?.reason || 'No payout mechanism'}
        />
      </div>

      {summary?.as_of && (
        <p className="text-xs text-[#062B1A]/40">
          Figures as of {new Date(summary.as_of).toLocaleString('en-CA')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFilter({ tab: tab.id })}
            className={cn(filterPillClass, filters.tab === tab.id && filterPillActiveClass)}
          >
            {tab.label}
            <span className="ml-1.5 opacity-70">({tabCounts[tab.id] ?? 0})</span>
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowFilters((open) => !open)}
          className={cn(filterPillClass, filterCount > 0 && filterPillActiveClass)}
        >
          <Filter className="mr-1.5 size-3.5" />
          Filters{filterCount > 0 ? ` (${filterCount})` : ''}
        </Button>
        {filterCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ ...DEFAULT_FILTERS, tab: filters.tab })}
            className="h-8 px-2 text-xs text-[#062B1A]/60"
          >
            <X className="mr-1 size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {showFilters && (
        <TableFilterCard>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label htmlFor="invoice-customer">Customer</Label>
              <select
                id="invoice-customer"
                value={filters.customer}
                onChange={(event) => setFilter({ customer: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="">All customers</option>
                {customers.map((customer) => (
                  <option key={customer.ref} value={customer.ref}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="invoice-status">Status</Label>
              <select
                id="invoice-status"
                value={filters.status}
                onChange={(event) => setFilter({ status: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="all">Any status</option>
                {['draft', 'sent', 'partial', 'paid', 'overdue', 'void'].map((status) => (
                  <option key={status} value={status}>
                    {invoiceStatusMeta(status).label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="invoice-from">From</Label>
              <Input
                id="invoice-from"
                type="date"
                value={filters.from}
                onChange={(event) => setFilter({ from: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="invoice-to">To</Label>
              <Input
                id="invoice-to"
                type="date"
                value={filters.to}
                onChange={(event) => setFilter({ to: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="invoice-number">Invoice number</Label>
              <Input
                id="invoice-number"
                type="search"
                value={filters.number}
                onChange={(event) => setFilter({ number: event.target.value })}
                placeholder="1001"
                className={cn('mt-1', adminInputClass)}
              />
            </div>
          </div>
        </TableFilterCard>
      )}

      {canDelete && (
        <BulkDeleteBar
          count={selection.count}
          noun="invoice"
          onDelete={() => setBulkOpen(true)}
          onClear={selection.clear}
        />
      )}

      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading ? (
        <TableSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <AccountingEmpty
          title={filterCount > 0 ? 'No invoices match these filters' : 'No invoices yet'}
          description={
            filterCount > 0
              ? 'Clear a filter to widen the search.'
              : 'Invoices raised here, and invoices issued from an application, both appear in this list.'
          }
          action={
            filterCount > 0 ? (
              <Button type="button" variant="outline" onClick={() => setFilters({ ...DEFAULT_FILTERS, tab: filters.tab })}>
                Clear filters
              </Button>
            ) : canCreate ? (
              <Button type="button" onClick={() => setCreateOpen(true)} className="bg-[#062B1A] text-white">
                <Plus className="mr-2 size-4" />
                Create an invoice
              </Button>
            ) : null
          }
        />
      ) : (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[1050px] text-left text-sm">
              <thead className="bg-[#062B1A] text-[10px] uppercase tracking-[0.035em] text-white">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        aria-label="Select every invoice on this page that can be deleted"
                        className="border-white/50 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-[#062B1A]"
                        checked={selection.allSelected}
                        disabled={selection.selectableCount === 0}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Due</th>
                  <th className="px-3 py-2.5 font-semibold">Date</th>
                  <th className="px-3 py-2.5 font-semibold">Number</th>
                  <th className="px-3 py-2.5 font-semibold">Customer</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Amount due</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                {rows.map((invoice) => {
                  const meta = invoiceStatusMeta(invoice.status);
                  return (
                    <tr key={invoice.ref} className="bg-white hover:bg-[#F7F8FA]/80">
                      {canDelete && (
                        <td className="px-3 py-3">
                          <Checkbox
                            aria-label={`Select invoice ${invoice.number}`}
                            checked={selection.isSelected(invoice)}
                            disabled={invoice.delete_effect?.allowed === false}
                            onCheckedChange={() => selection.toggle(invoice)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                      </td>
                      <td className="px-3 py-3">
                        {invoice.due_phrase ? (
                          <>
                            <div
                              className={cn(
                                'text-xs font-medium',
                                invoice.status === 'overdue' ? 'text-red-700' : 'text-[#062B1A]/70'
                              )}
                            >
                              {invoice.due_phrase}
                            </div>
                            <div className="text-[11px] text-[#062B1A]/40">{invoice.due_date || '—'}</div>
                          </>
                        ) : (
                          <div className="text-xs text-[#062B1A]/70">{invoice.due_date || '—'}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-[#062B1A]/70">{invoice.issue_date || '—'}</td>
                      <td className="px-3 py-3">
                        {/* The number is the way in to the invoice, because it
                            is the thing people already point at when they mean
                            "that one". */}
                        <Link
                          to={invoiceDetailPath(invoice.ref)}
                          className="font-mono text-xs font-semibold text-[#062B1A] hover:underline"
                        >
                          {invoice.number}
                        </Link>
                        <div className="text-[11px] text-[#062B1A]/40">{invoice.origin_label}</div>
                      </td>
                      <td className="px-3 py-3">
                        {invoice.customer.link ? (
                          <Link
                            to={invoice.customer.link}
                            className="text-sm font-medium text-[#062B1A] hover:underline"
                          >
                            {invoice.customer.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-[#062B1A]">{invoice.customer.name}</span>
                        )}
                        <div className="text-[11px] text-[#062B1A]/40">
                          {invoice.customer.source_label}
                          {invoice.paid_cents > 0 && invoice.amount_due_cents > 0
                            ? ` · ${formatCents(invoice.paid_cents)} paid`
                            : ''}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-xs text-[#062B1A]/70">
                        {invoice.total_display}
                      </td>
                      <td className="px-3 py-3 text-right text-sm font-semibold text-[#062B1A]">
                        {invoice.amount_due_display}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Where the invoice actually is now. Converting an
                              applicant moves their documents to the student,
                              and the application page stops showing them — so
                              once there is a student, that is the only page
                              worth offering. */}
                          {invoice.student_id ? (
                            <Button asChild variant="ghost" size="sm" title="Open the student this invoice belongs to">
                              <Link to={`/admin/students/${invoice.student_id}`}>
                                <ExternalLink className="size-3.5" />
                                <span className="sr-only">Open the student</span>
                              </Link>
                            </Button>
                          ) : invoice.application_id ? (
                            <Button asChild variant="ghost" size="sm" title="Open the application this invoice came from">
                              <Link to={`/admin/applications/${invoice.application_id}`}>
                                <ExternalLink className="size-3.5" />
                                <span className="sr-only">Open the application</span>
                              </Link>
                            </Button>
                          ) : null}
                          <InvoiceActionsMenu
                            invoice={invoice}
                            permissions={permissions}
                            actions={workflow.actions}
                            onDuplicated={(created) => navigate(invoiceDetailPath(created.ref))}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageInfo && pageInfo.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgba(6,43,26,0.07)] bg-white px-4 py-3 text-xs text-[#062B1A]/60">
              <span>{pageInfo.total_count} invoices</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page <= 1}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page - 1) }))}
                >
                  Previous
                </Button>
                <span>
                  Page {pageInfo.current_page} of {pageInfo.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page >= pageInfo.total_pages}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page + 1) }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <InvoiceFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        customers={customers}
        items={items}
        taxes={taxes}
        onSaved={() => load({ quiet: true })}
      />

      <BulkDeleteDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        noun="invoice"
        rows={selection.selectedRows}
        labelOf={(row) => `${row.number} · ${row.customer.name} · ${row.total_display}`}
        remove={(row) => deleteAccountingInvoice(row.ref)}
        onFinished={() => {
          selection.clear();
          load({ quiet: true });
        }}
      />

      {workflow.dialogs}
    </AccountingShell>
  );
}
